import React, { useState } from 'react';
import { ListPageHeader } from '../../commonComponents/ListPageHeader';
import { PromotionsTab } from './PromotionsTab';
import { PromotionFormModal } from './PromotionFormModal';
import { DisablePromotionModal } from './DisablePromotionModal';
import { PayoutUploadModal } from './PayoutUploadModal';
import { useDownloadPayoutReport } from '../../../domain/Promotions';
import { showErrorMessage } from '../../../utils';
import { triggerBlobDownload } from '../../../utils/downloadBlob';

/**
 * Pro Programs → Promotions, at `/pro-programs/promotions`.
 *
 * Owns the modal state machine for the whole promotion lifecycle — create,
 * edit, disable, reactivate, and payout upload — because every one of those is
 * launched from a row in the list below and has to close back to it.
 *
 * This and Challenges used to be two tabs of one component. They are separate
 * routes now, which is what lets this page keep the fixed reading column: the
 * list is a compact table, while Challenges is a full-width page.
 */
export const PromotionsPage = () => {
  // modal = { type: 'create' | 'edit' | 'disable' | 'reactivate' | 'payout', promotion } | null
  const [modal, setModal] = useState(null);

  const closeModal = () => setModal(null);
  const openCreate = () => setModal({ type: 'create', promotion: null });
  const openEdit = (promotion) => setModal({ type: 'edit', promotion });
  const openDisable = (promotion) => setModal({ type: 'disable', promotion });
  const openReactivate = (promotion) => setModal({ type: 'reactivate', promotion });
  const openPayout = (promotion) => setModal({ type: 'payout', promotion });

  const downloadReport = useDownloadPayoutReport({
    onSuccess: ({ blob, filename }) => triggerBlobDownload(blob, filename),
    onError: () => showErrorMessage({ message: 'Could not download the payout report.' }),
  });

  const handleViewReport = (promotion, batch, outcome) =>
    downloadReport.mutate({
      promotionId: promotion.id,
      batchId: batch.batchId,
      outcome,
    });

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: '24px 16px',
      }}
    >
      <ListPageHeader title="Promotions" />

      <PromotionsTab
        onAddPromotion={openCreate}
        onEditPromotion={openEdit}
        onAddPayout={openPayout}
        onReactivate={openReactivate}
        onViewReport={handleViewReport}
      />

      {(modal?.type === 'create' || modal?.type === 'edit') && (
        <PromotionFormModal
          mode={modal.type}
          promotion={modal.promotion}
          onClose={closeModal}
          onRequestDisable={openDisable}
        />
      )}

      {modal?.type === 'disable' && (
        <DisablePromotionModal promotion={modal.promotion} onClose={closeModal} />
      )}

      {modal?.type === 'reactivate' && (
        <DisablePromotionModal
          promotion={modal.promotion}
          mode="reactivate"
          onClose={closeModal}
        />
      )}

      {modal?.type === 'payout' && (
        <PayoutUploadModal promotion={modal.promotion} onClose={closeModal} />
      )}
    </div>
  );
};

export default PromotionsPage;
